import { useCallback, useEffect, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { SET_DEFAULT_TERMINAL, SET_STATION_CUSTOMER_DISPLAY_ENABLED } from "src/constants/mutations";
import { GET_STATIONS_BY_STORE, GET_TERMINALS_BY_STORE } from "src/constants/queries";
import { successToast, failureToast } from "src/utils/toasts";
import { BtnLoader } from "src/Loader";
import { Station } from "src/interfaces/station.interface";
import { Terminal } from "src/interfaces/terminal.interface";
import CustomSelect from "src/components/common/CustomSelect";

const StationsSettings = () => {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const [stations, setStations] = useState<Station[]>([]);
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  // Map to track selected terminal for each station (for UI)
  const [selectedTerminals, setSelectedTerminals] = useState<Map<string, string>>(new Map());

  const fetchData = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    
    setIsLoadingData(true);
    try {
      // Fetch stations and terminals in parallel
      const [stationsResponse, terminalsResponse] = await Promise.all([
        axBe.post(GRAPHQL_ENDPOINT, {
          query: GET_STATIONS_BY_STORE,
          variables: {
            storeCorrelationId: hasRestaurant.correlationId,
          },
        }),
        axBe.post(GRAPHQL_ENDPOINT, {
          query: GET_TERMINALS_BY_STORE,
          variables: {
            storeCorrelationId: hasRestaurant.correlationId,
          },
        }),
      ]);

      if (stationsResponse.data?.errors?.length) {
        throw stationsResponse.data.errors;
      }
      if (terminalsResponse.data?.errors?.length) {
        throw terminalsResponse.data.errors;
      }

      const fetchedStations = stationsResponse.data?.data?.getStationsByStore || [];
      const fetchedTerminals = terminalsResponse.data?.data?.getTerminalsByStore || [];

      setStations(fetchedStations);
      setTerminals(fetchedTerminals);

      // Initialize selectedTerminals map with current default terminal assignments
      const initialSelections = new Map<string, string>();
      fetchedStations.forEach((station: Station) => {
        if (station.defaultTerminalCorrelationId) {
          initialSelections.set(station.correlationId, station.defaultTerminalCorrelationId);
        }
      });
      setSelectedTerminals(initialSelections);
    } catch (error) {
      console.error("Error fetching stations and terminals:", error);
      failureToast("Failed to load stations and terminals");
    } finally {
      setIsLoadingData(false);
    }
  }, [axBe, hasRestaurant?.correlationId]);

  useEffect(() => {
    if (hasRestaurant?.correlationId) {
      fetchData();
    }
  }, [fetchData, hasRestaurant?.correlationId]);

  const handleTerminalSelect = (stationCorrelationId: string, terminalCorrelationId: string) => {
    const newSelections = new Map(selectedTerminals);
    newSelections.set(stationCorrelationId, terminalCorrelationId);
    setSelectedTerminals(newSelections);
  };

  const handleSetDefaultTerminal = async (stationCorrelationId: string, terminalCorrelationId: string | null) => {
    if (!hasRestaurant?.correlationId) return;

    setLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: SET_DEFAULT_TERMINAL,
        variables: {
          stationCorrelationId: stationCorrelationId,
          terminalCorrelationId: terminalCorrelationId,
        },
      });

      if (data?.errors?.length) {
        throw data.errors;
      }

      if (terminalCorrelationId === null) {
        successToast("Default terminal cleared successfully!");
      } else {
        successToast("Default terminal set successfully!");
      }

      // Refresh stations to get updated data
      await fetchData();
    } catch (error: any) {
      console.error("Error setting default terminal:", error);
      const errorMessage = error?.message || error?.[0]?.message || "Failed to set default terminal";
      failureToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClearDefaultTerminal = async (stationCorrelationId: string) => {
    await handleSetDefaultTerminal(stationCorrelationId, null);
  };

  const handleCustomerDisplayToggle = async (stationCorrelationId: string, enabled: boolean) => {
    if (!hasRestaurant?.correlationId) return;

    setLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: SET_STATION_CUSTOMER_DISPLAY_ENABLED,
        variables: {
          stationCorrelationId,
          customerDisplayEnabled: enabled,
        },
      });

      if (data?.errors?.length) {
        throw data.errors;
      }

      setStations((current) =>
        current.map((station) =>
          station.correlationId === stationCorrelationId
            ? { ...station, customerDisplayEnabled: enabled }
            : station
        )
      );
      successToast(enabled ? "Customer display enabled." : "Customer display disabled.");
    } catch (error: any) {
      console.error("Error updating customer display setting:", error);
      const errorMessage = error?.message || error?.[0]?.message || "Failed to update customer display setting";
      failureToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get terminal name by correlation ID
  const getTerminalName = (terminalCorrelationId: string | undefined): string => {
    if (!terminalCorrelationId) return "None";
    const terminal = terminals.find((t) => t.correlationId === terminalCorrelationId);
    return terminal?.name || "Unknown Terminal";
  };

  // Get active terminals only
  const activeTerminals = terminals.filter((t) => t.active);

  // Prepare terminal options for dropdown
  const getTerminalOptions = () => {
    return [
      { value: "", label: "Select Terminal" },
      ...activeTerminals.map((terminal) => ({
        value: terminal.correlationId,
        label: terminal.name,
      })),
    ];
  };

  if (isLoadingData) {
    return (
      <div className="w-full max-w-[760px] flex justify-center items-center py-12">
        <BtnLoader />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]">
        <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-2">Stations</h2>
        <p className="text-[14px] text-[#6b7280] mb-6">
          Manage default terminals for each station. The default terminal will be used when processing orders at that station.
        </p>

        {stations.length === 0 ? (
          <p className="text-center py-6 text-[14px] text-[#6b7280]">No stations found for this store.</p>
        ) : (
          <div className="space-y-4">
            {stations.map((station) => {
              const currentDefaultTerminalId = station.defaultTerminalCorrelationId;
              const selectedTerminalId = selectedTerminals.get(station.correlationId) || currentDefaultTerminalId || "";
              const hasDefaultTerminal = !!currentDefaultTerminalId;

              return (
                <div
                  key={station.correlationId}
                  className="rounded-[12px] border border-[#e5e7eb] p-4"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <h3 className="text-[14px] font-medium text-[#111827]">{station.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${
                      station.active ? "bg-green-100 text-green-800" : "bg-[#f3f4f6] text-[#6b7280]"
                    }`}>
                      {station.active ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div className="rounded-[12px] border border-[#e5e7eb] p-4">
                      <p className="mb-2 text-[14px] font-medium text-[#4b5563]">Customer display</p>
                      <label
                        className={`flex items-center gap-3 ${loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[16px] font-semibold text-white ${
                            station.customerDisplayEnabled ? "bg-[#FD7657]" : "border border-[#d1d5db] bg-white"
                          }`}
                        >
                          {station.customerDisplayEnabled ? "✓" : ""}
                        </span>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={!!station.customerDisplayEnabled}
                          disabled={loading}
                          onChange={(event) => handleCustomerDisplayToggle(station.correlationId, event.target.checked)}
                        />
                        <span className="text-[14px] text-[#111827]">
                          Show live order and payment status on this station&apos;s second screen.
                        </span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-[14px] font-medium text-[#4b5563] mb-2">Default Terminal</label>
                      {hasDefaultTerminal && (
                        <p className="mb-2 text-[14px] text-[#6b7280]">
                          Current: <span className="font-medium text-[#111827]">{getTerminalName(currentDefaultTerminalId)}</span>
                        </p>
                      )}
                      <CustomSelect
                        options={getTerminalOptions()}
                        value={selectedTerminalId}
                        onChange={(value) => handleTerminalSelect(station.correlationId, value)}
                        placeholder="Select Terminal"
                      />
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleSetDefaultTerminal(station.correlationId, selectedTerminalId ? selectedTerminalId : null)}
                        disabled={loading || !selectedTerminalId}
                        className={`px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2 ${
                          (loading || !selectedTerminalId) ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        {hasDefaultTerminal && selectedTerminalId === currentDefaultTerminalId ? "Update" : "Set Default Terminal"}
                      </button>
                      {hasDefaultTerminal && (
                        <button
                          type="button"
                          onClick={() => handleClearDefaultTerminal(station.correlationId)}
                          disabled={loading}
                          className={`text-[13px] font-medium text-[#6b7280] bg-transparent border-none cursor-pointer hover:text-[#111827] ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StationsSettings;
